import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { authService, UserProfile } from '../services/auth';

const PROFILE_LOAD_TIMEOUT_MS = 8_000;

function isAbortOrTimeout(error: unknown): boolean {
  if (!error) return false;
  const name = typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name?: string }).name ?? '')
    : '';
  const message = error instanceof Error ? error.message : String(error);
  return name === 'AbortError' || /abort|Carga de perfil/i.test(message);
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
  abort?: AbortController
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      abort?.abort();
      reject(new Error(`${label} (${ms}ms)`));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  userPermissions: Record<string, boolean>;
  signIn: (email: string, password: string) => Promise<void>;
  signUpComensal: (
    email: string,
    password: string
  ) => Promise<{ user: User | null; session: Session | null }>;
  confirmSignupEmail: (email: string, token: string) => Promise<void>;
  resendSignupConfirmation: (email: string) => Promise<void>;
  confirmPhoneSms: (phoneE164: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: (options?: { silent?: boolean }) => Promise<void>;
  hasPermission: (permissionCode: string) => Promise<boolean>;
  hasPermissionSync: (permissionCode: string) => boolean;
  hasRole: (role: 'owner' | 'master' | 'admin' | 'user') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userPermissions, setUserPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const profileLoadInFlightRef = useRef<Promise<void> | null>(null);
  const profileLoadUserIdRef = useRef<string | null>(null);
  /** Evita segunda carga desde onAuthStateChange mientras signIn ya cargó el perfil. */
  const signInHandledProfileRef = useRef(false);

  const loadProfileAndPermissions = async (
    userId: string,
    authUser?: Pick<User, 'id' | 'email' | 'user_metadata'> | null,
    accessToken?: string | null
  ): Promise<UserProfile | null> => {
    if (profileLoadInFlightRef.current && profileLoadUserIdRef.current === userId) {
      return profileLoadInFlightRef.current;
    }

    profileLoadUserIdRef.current = userId;
    const task = (async (): Promise<UserProfile | null> => {
      let loaded: UserProfile | null = null;
      try {
        // Liberar el lock interno de supabase-js tras SIGNED_IN.
        await new Promise<void>((resolve) => setTimeout(resolve, 50));

        const loadOnce = (signal?: AbortSignal) =>
          authService.getProfileByUserId(userId, {
            email: authUser?.email,
            userMetadata: authUser?.user_metadata as Record<string, unknown> | undefined,
            signal,
            accessToken,
          });

        const loadWithTimeout = async () => {
          const abort = new AbortController();
          return withTimeout(loadOnce(abort.signal), PROFILE_LOAD_TIMEOUT_MS, 'Carga de perfil', abort);
        };

        let userProfile: UserProfile | null = null;
        try {
          userProfile = await loadWithTimeout();
        } catch (firstError) {
          if (!isAbortOrTimeout(firstError)) throw firstError;
          await new Promise<void>((resolve) => setTimeout(resolve, 300));
          userProfile = await loadWithTimeout();
        }

        if (!userProfile) {
          console.warn('⚠️ AuthProvider: No se pudo cargar el perfil del usuario.');
          setProfile(null);
          setUserPermissions({});
          return null;
        }

        loaded = userProfile;
        setProfile(userProfile);

        if (userProfile.role === 'owner' || userProfile.role === 'master') {
          setUserPermissions({});
          return loaded;
        }

        void (async () => {
          try {
            const permisos = await authService.getUserPermisos(userId);
            const perms: Record<string, boolean> = {};
            for (const up of permisos) {
              const codigo = up.permiso?.codigo;
              if (codigo) perms[codigo] = true;
            }
            setUserPermissions(perms);
          } catch (permError) {
            console.error('❌ AuthProvider: Error al cargar permisos', permError);
            setUserPermissions({});
          }
        })();
        return loaded;
      } catch (error) {
        console.error('❌ AuthProvider: Error al cargar perfil', error);
        setProfile(null);
        setUserPermissions({});
        return null;
      } finally {
        setLoading(false);
        if (profileLoadUserIdRef.current === userId) {
          profileLoadInFlightRef.current = null;
        }
      }
    })();

    profileLoadInFlightRef.current = task;
    return task;
  };

  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      console.warn('[Auth] Timeout de arranque (15s): se muestra la UI sin sesión inicial.');
      setLoading(false);
    }, 15_000);

    const clearBootTimeout = () => {
      clearTimeout(timeoutId);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        if (signInHandledProfileRef.current) {
          signInHandledProfileRef.current = false;
          clearBootTimeout();
          return;
        }
        const uid = nextSession.user.id;
        const token = nextSession.access_token;
        setTimeout(() => {
          if (cancelled) return;
          void loadProfileAndPermissions(uid, nextSession.user, token).finally(() => clearBootTimeout());
        }, 0);
      } else {
        setProfile(null);
        setUserPermissions({});
        setLoading(false);
        clearBootTimeout();
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    signInHandledProfileRef.current = true;
    try {
      const { user: authUser, session: newSession } = await authService.signIn(email, password);
      if (authUser && newSession) {
        setSession(newSession);
        setUser(authUser);
        const p = await loadProfileAndPermissions(
          authUser.id,
          authUser,
          newSession.access_token
        );
        if (!p) {
          throw new Error(
            'No se encontró tu perfil de usuario. Contacta al administrador.'
          );
        }
      } else if (authUser) {
        setUser(authUser);
        const p = await loadProfileAndPermissions(authUser.id, authUser);
        if (!p) {
          throw new Error(
            'No se encontró tu perfil de usuario. Contacta al administrador.'
          );
        }
      } else {
        setLoading(false);
        signInHandledProfileRef.current = false;
      }
    } catch (error) {
      signInHandledProfileRef.current = false;
      setLoading(false);
      throw error;
    }
  };

  const signUpComensal = async (email: string, password: string) => {
    const { user: newUser, session: newSession } = await authService.signUpComensal(email, password);
    if (newSession?.user) {
      setSession(newSession);
      setUser(newSession.user);
      await loadProfileAndPermissions(newSession.user.id, newSession.user, newSession.access_token);
    } else {
      setLoading(false);
    }
    return { user: newUser, session: newSession };
  };

  const confirmSignupEmail = async (email: string, token: string) => {
    const { session: newSession, user: authUser } = await authService.verifySignupEmailOtp(email, token);
    if (!newSession?.user || !authUser) {
      throw new Error('No se pudo iniciar sesión tras verificar el correo.');
    }
    setSession(newSession);
    setUser(authUser);
    await loadProfileAndPermissions(authUser.id, authUser, newSession.access_token);
  };

  const resendSignupConfirmation = async (email: string) => {
    await authService.resendSignupConfirmationEmail(email);
  };

  const confirmPhoneSms = async (phoneE164: string, token: string) => {
    const { session: newSession, user: authUser } = await authService.verifyPhoneOtp(phoneE164, token);
    if (!newSession?.user || !authUser) {
      throw new Error('No se pudo confirmar el teléfono.');
    }
    setSession(newSession);
    setUser(authUser);
    await loadProfileAndPermissions(authUser.id, authUser, newSession.access_token);
  };

  const signOut = async () => {
    try {
      await authService.signOut();
    } catch (error) {
      console.error('❌ AuthProvider: Error en signOut', error);
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
      setUserPermissions({});
      setLoading(false);
      signInHandledProfileRef.current = false;
      profileLoadInFlightRef.current = null;
      profileLoadUserIdRef.current = null;
    }
  };

  const refreshProfile = async (options?: { silent?: boolean }) => {
    if (user) {
      if (!options?.silent) {
        setLoading(true);
      }
      await loadProfileAndPermissions(user.id, user, session?.access_token);
    }
  };

  const hasPermission = async (permissionCode: string): Promise<boolean> => {
    if (!user || !profile) return false;
    return await authService.currentUserHasPermission(permissionCode);
  };

  const hasPermissionSync = (permissionCode: string): boolean => {
    if (!profile) return false;
    if (profile.role === 'owner' || profile.role === 'master') return true;
    return !!userPermissions[permissionCode];
  };

  const hasRole = (role: 'owner' | 'master' | 'admin' | 'user'): boolean => {
    if (!profile) return false;

    const roleHierarchy: Record<string, number> = {
      owner: 4,
      master: 3,
      admin: 2,
      user: 1,
    };

    const userRoleLevel = roleHierarchy[profile.role] || 0;
    const requiredRoleLevel = roleHierarchy[role] || 0;

    return userRoleLevel >= requiredRoleLevel;
  };

  const value: AuthContextType = {
    session,
    user,
    profile,
    loading,
    userPermissions,
    signIn,
    signUpComensal,
    confirmSignupEmail,
    resendSignupConfirmation,
    confirmPhoneSms,
    signOut,
    refreshProfile,
    hasPermission,
    hasPermissionSync,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}
